import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logo from "@/assets/moneylink_logo.svg";
import { useLoginContext } from "@/features/auth/contexts/login-context";
import { useNewsContext } from "@/features/news/contexts/news-context";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { useGetUsers } from "@/features/user/hooks/use-get-users";
import { useGetNews } from "@/features/news/hooks/use-get-news";
import { NewsModal } from "@/features/news/components/news-modal";
import { SettingsModal } from "@/features/settings/components/settings-modal";
import { UpdateUserInfoModal } from "@/features/user/components/update-user-info-modal";
import { UpdateIdModal } from "@/features/user/components/update-id-modal";
import { UpdatePassModal } from "@/features/user/components/update-pass-modal";
import { UpdateCompleteModal } from "@/features/user/components/update-complete-modal";
import { TwoStepAuthSetModal } from "@/features/two-step-authentication/components/two-step-authentication-set-modal";
import { TwoStepAuthSetCodeModal } from "@/features/two-step-authentication/components/two-step-authentication-set-code-modal";
import { TwoStepAuthSetCompleteModal } from "@/features/two-step-authentication/components/two-step-authentication-complete-modal";
import { LoginHistoryModal } from "@/features/user/components/login-history-modal";
import { DeleteUserModal } from "@/features/user/components/delete-user-modal";
import { MailSendModal } from "@/features/user/components/mail-send-modal";
import { useTradeJumpContext } from "@/features/trade-jump/contexts/trade-jump-context";
import { UserIntegrationInputModal } from "@/features/user/components/users-integration-input-modal";
import { UserIntegrationConfirmModal } from "@/features/user/components/users-integration-confirm-modal";
import { UserIntegrationCompletedModal } from "@/features/user/components/users-integration-completed-modal";
import { useReceiveMailsContext } from "@/features/user/contexts/receive-mails-context";
import { MailSettingsModal } from "@/features/user/components/mail-settings-modal";

type Props = {
  doAction: () => void;
};

export const Header = ({ doAction }: Props) => {
  const {
    soriId,
    setEmail,
    setUserName,
    setSoriId,
  } = useLoginContext();
  const {
    setNews,
  } = useNewsContext();
  const {
    openHomeModal,
    setOpenHomeModal,
    setKeepModal,
  } = useModalContext();
  const { getUsers } = useGetUsers();
  const { getNews } = useGetNews();
  const {
    syncJump,
    setSyncJump,
  } = useTradeJumpContext();
  const {
    setIsReceiveNoticeMail,
    setIsReceiveErrorMail,
  } = useReceiveMailsContext();

  // ユーザ情報の取得
  useEffect(() => {
    const fetchUsers = async () => {
      const res = await getUsers();
      if (res) {
        if (soriId === "" || syncJump === undefined) {
          // soriId, syncJumpが初期値
          // → リダイレクト後など画面描画とともにモーダル表示される場合があるため、モーダルリセットされないように制御
          setKeepModal(true);
        }
        else {
          // 上記以外の場合、画面表示中にBroadcastChannelでsoriId, syncJumpが更新された
          // → 別タブで別アカウントが起動したと検知できるため、モーダルリセットを許可
          setKeepModal(false);
        }
        setSoriId(res.sori_id);
        setEmail(res.owner_id);
        setUserName(res.owner_name);
        setSyncJump(res.is_kcloud_auth);
        setIsReceiveNoticeMail(res.is_receive_mail);
        setIsReceiveErrorMail(res.is_receive_error_mail);
      }
    };
    fetchUsers();
  }, [soriId, syncJump]);

  useEffect(() => {
    if (syncJump === undefined || syncJump) return;
    const fetchNews = async () => {
      const res = await getNews();
      if (res) {
        setNews(res);
      }
    };
    fetchNews();
  }, [soriId, syncJump]);

  const [isValid, setIsValid] = useState(false);

  return (
    <>
      <header className="hdr_global">
        <h1>
          <Link to="/home">
            <img src={logo} alt="MoneyLink" />
          </Link>
        </h1>

        {!syncJump && (
        <BaseRoundButton
          className="open-modal btn-bell"
          onClick={() => {
            setOpenHomeModal("News");
          }}
          title="お知らせ"
        />
        )}

        <BaseRoundButton
          className="open-modal btn-account"
          onClick={() => {
            setOpenHomeModal("Settings");
          }}
          title="アカウント"
        />
      </header>
      {openHomeModal === "News" && (
        <NewsModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
        />
      )}

      {openHomeModal === "Settings" && (
        <SettingsModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
        />
      )}

      {openHomeModal === "UserInfo" && (
        <UpdateUserInfoModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
          onTapCompleteModal={() => {
            setOpenHomeModal("UserInfoComplete");
          }}
        />
      )}

      {openHomeModal === "UserInfoComplete" && (
        <UpdateCompleteModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
        />
      )}

      {openHomeModal === "UserId" && (
        <UpdateIdModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
          onTapCompleteModal={() => {
            setOpenHomeModal("MailSend");
          }}
        />
      )}

      {openHomeModal === "UserPassword" && (
        <UpdatePassModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
          onTapCompleteModal={() => {
            setOpenHomeModal("UserPasswordComplete");
          }}
        />
      )}

      {openHomeModal === "UserPasswordComplete" && (
        <UpdateCompleteModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
        />
      )}

      {openHomeModal === "MailSettings" && (
        <MailSettingsModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
        />
      )}

      {openHomeModal === "TwoStepAuth" && (
        <TwoStepAuthSetModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
          onTapCompleteModal={() => {
            setOpenHomeModal("TwoStepAuthComplete");
          }}
          setIsValid={setIsValid}
        />
      )}

      {openHomeModal === "TwoStepAuthSetCode" && (
        <TwoStepAuthSetCodeModal
          onCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
          onCompleteModal={() => {
            setOpenHomeModal("TwoStepAuthComplete");
          }}
          setIsValid={setIsValid}
        />
      )}

      {openHomeModal === "TwoStepAuthComplete" && (
        <TwoStepAuthSetCompleteModal
          isValid={isValid}
          onCloseModal={() => {
            setOpenHomeModal(undefined);}}
        />
      )}

      {openHomeModal === "UsersIntegrationInput" && (
        <UserIntegrationInputModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
          onTapCompleteModal={() => {
            setOpenHomeModal("UsersIntegrationConfirm");
          }}
        />
      )}

      {openHomeModal === "UsersIntegrationConfirm" && (
        <UserIntegrationConfirmModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
          onTapCompleteModal={() => {
            setOpenHomeModal("UsersIntegrationCompleted");
          }}
        />
      )}

      {openHomeModal === "UsersIntegrationCompleted" && (
        <UserIntegrationCompletedModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
            doAction();
          }}
        />
      )}

      {openHomeModal === "LoginHistory" && (
        <LoginHistoryModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
        />
      )}

      {openHomeModal === "DeleteUser" && (
        <DeleteUserModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
        />
      )}

      {openHomeModal === "MailSend" && (
        <MailSendModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
          }}
        />
      )}
    </>
  );
};
